import type { JSX } from "react";
import type {
  CaseGenerationBatchDto,
  CaseGenerationBatchStatus,
  Difficulty,
} from "../api/types";

interface AdminGenerationBatchesProps {
  batches: readonly CaseGenerationBatchDto[];
}

function statusLabel(status: CaseGenerationBatchStatus): string {
  switch (status) {
    case "Queued":
      return "I kø";
    case "Running":
      return "Genererer";
    case "Completed":
      return "Ferdig";
    case "PartiallyCompleted":
      return "Delvis ferdig";
    case "Failed":
      return "Feilet";
    default: {
      const exhaustiveCheck: never = status;
      throw new Error(`Unhandled batch status: ${String(exhaustiveCheck)}`);
    }
  }
}

function difficultyLabel(difficulty: Difficulty): string {
  switch (difficulty) {
    case "Easy":
      return "Lett";
    case "Medium":
      return "Middels";
    case "Hard":
      return "Krevende";
    default: {
      const exhaustiveCheck: never = difficulty;
      throw new Error(`Unhandled difficulty: ${String(exhaustiveCheck)}`);
    }
  }
}

function topicLabel(topicSlug: string | null): string {
  if (topicSlug === null)
    return "Varierte tema";
  const words = topicSlug.replace(/-/g, " ");
  return `${words.charAt(0).toLocaleUpperCase("nb-NO")}${words.slice(1)}`;
}

function progressPercent(completedCount: number, requestedCount: number): number {
  if (requestedCount <= 0)
    return 0;
  return Math.round((completedCount / requestedCount) * 100);
}

/** Shows live progress for recent durable generation batches. */
export function AdminGenerationBatches({
  batches,
}: AdminGenerationBatchesProps): JSX.Element | null {
  if (batches.length === 0)
    return null;

  return (
    <section className="generation-batches" aria-labelledby="generation-batches-title">
      <header className="queue-heading">
        <div>
          <p className="eyebrow">Bakgrunnsjobber</p>
          <h2 id="generation-batches-title">Genereringsstatus</h2>
        </div>
        <span>{batches.length} siste</span>
      </header>
      <ul>
        {batches.map((batch) => {
          const percentage = progressPercent(batch.generatedCount, batch.requestedCount);
          return (
            <li key={batch.id}>
              <div className="batch-summary">
                <div>
                  <strong>{batch.requestedCount.toLocaleString("nb-NO")} caser</strong>
                  <span>{topicLabel(batch.topicSlug)} · {difficultyLabel(batch.difficulty)}</span>
                </div>
                <span className={`batch-status status-${batch.status.toLowerCase()}`}>
                  {statusLabel(batch.status)}
                </span>
              </div>
              <progress
                max={Math.max(batch.requestedCount, 1)}
                value={batch.generatedCount}
                aria-label={`Fremdrift for batch med ${batch.requestedCount} caser`}
              />
              <div className="batch-counts">
                <span>{percentage} %</span>
                <span>{batch.generatedCount} laget</span>
                {batch.failedCount > 0 && <span className="field-error">{batch.failedCount} feilet</span>}
              </div>
              {batch.error !== null && <p className="batch-error">{batch.error}</p>}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
